import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <div className="bg-[#0F172A] py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-serif">
            Privacy Policy
          </h1>
          <p className="text-white/70 mt-4">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Introduction</h2>
            <p className="text-[#475569] leading-relaxed">
              Next Chapter Moves NC ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website nextchaptermovesnc.net and use our services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Information We Collect</h2>
            <p className="text-[#475569] leading-relaxed mb-4">
              We collect information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-[#475569] space-y-2">
              <li>Fill out our contact form (name, email address, phone number, message)</li>
              <li>Download our Next Chapter Guide (name, email address)</li>
              <li>Subscribe to our communications</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">How We Use Your Information</h2>
            <p className="text-[#475569] leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-[#475569] space-y-2">
              <li>Respond to your inquiries and provide customer service</li>
              <li>Send you the requested guide and related resources</li>
              <li>Communicate with you about our real estate services</li>
              <li>Improve our website and services</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Third-Party Services</h2>
            <p className="text-[#475569] leading-relaxed mb-4">
              We use the following third-party services to help us operate our website and manage communications:
            </p>
            <ul className="list-disc pl-6 text-[#475569] space-y-2">
              <li><strong>HubSpot:</strong> Customer relationship management and email communications</li>
            </ul>
            <p className="text-[#475569] leading-relaxed mt-4">
              These services have their own privacy policies governing their use of your information.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Data Retention</h2>
            <p className="text-[#475569] leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. You may request deletion of your data at any time by contacting us.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Your Rights</h2>
            <p className="text-[#475569] leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-[#475569] space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of marketing communications</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Cookies and Tracking</h2>
            <p className="text-[#475569] leading-relaxed">
              Our website uses cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic. We use HubSpot tracking to understand how visitors interact with our site. You can control cookies through your browser settings.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Data Security</h2>
            <p className="text-[#475569] leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Children's Privacy</h2>
            <p className="text-[#475569] leading-relaxed">
              Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Changes to This Policy</h2>
            <p className="text-[#475569] leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#0F172A] font-serif mb-4">Contact Us</h2>
            <p className="text-[#475569] leading-relaxed">
              If you have questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="mt-4 p-6 bg-white border-l-4 border-[#D4AF37]">
              <p className="text-[#0F172A] font-semibold">Next Chapter Moves NC</p>
              <p className="text-[#475569]">Knightdale, North Carolina</p>
              <p className="text-[#475569]">Email: <a href="mailto:info@nextchaptermovesnc.net" className="text-[#D4AF37] hover:underline">info@nextchaptermovesnc.net</a></p>
              <p className="text-[#475569]">Phone: <a href="tel:919-879-5636" className="text-[#D4AF37] hover:underline">(919) 879-5636</a></p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#0F172A] py-8 px-6 text-center">
        <p className="text-[#94A3B8] text-sm">
          © {new Date().getFullYear()} Next Chapter Moves NC. All rights reserved.
        </p>
      </div>
    </div>
  );
}
